import { UntypedFormControl, ValidationErrors, AbstractControl, ValidatorFn } from '@angular/forms';
import * as _ from 'lodash';
import { cleanPortalDid, isValidUSNumber, isValidInternationalAndUSNumber, isValidInternationalAndUSNumberLenient, isValidInternationalOrLocalPhoneNumber, isValidE164WithoutPlus } from '@nuso/common/util-did';
import { checkSelectValues, emailRegex } from '@nuso/forms/util';
import { isAllSequentialDigits } from '@nuso/common/util-password';


export function validateUSPhoneNumber(control: UntypedFormControl): ValidationErrors | null {
  if ( !control.value ) {
    return null;
  }

  const did = cleanPortalDid(control.value);
  if (!isValidUSNumber(did)) {
    return {
      usPhoneNumber: true
    }
  }

  return null;
}

export function validateInternationalAndUSPhoneNumber(control: UntypedFormControl): ValidationErrors | null {
  if ( !control.value ) {
    return null;
  }

  if (!isValidInternationalAndUSNumber(control.value)) {
    return {
      phoneNumber: true
    }
  }

  return null;
}

export function validateE164PhoneNumber(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  if (!control.value.startsWith('+') || !isValidInternationalAndUSNumber(control.value)) {
    return {
      phoneNumber: true
    };
  }

  return null;
}

export function validateE164WithoutPlus(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  if (!isValidE164WithoutPlus(control.value)) {
    return {
      e164WithoutPlus: true
    }
  }

  return null;
}

export function validateInternationalAndUSPhoneNumberLenient(control: UntypedFormControl): ValidationErrors | null {
  if ( !control.value ) {
    return null;
  }

  if (!isValidInternationalAndUSNumberLenient(control.value)) {
    return {
      phoneNumber: true
    }
  }

  return null;
}

export function validateInternationalOrLocalPhoneNumberWithCountry(countryControl: AbstractControl): ValidatorFn | null {
  let subscribe = false;

  return (control: AbstractControl): {[key: string]: any} | null => {
    if (!subscribe) {
      subscribe = true;
      countryControl.valueChanges.subscribe(() => {
        control.updateValueAndValidity();
      });
    }

    if (!control.value) {
      return null;
    }

    if (!isValidInternationalOrLocalPhoneNumber(control.value, countryControl.value)) {
      return {
        'phoneNumber': true
      }
    }
    return null;

  }
}

export function validateIPAddress(control: UntypedFormControl): ValidationErrors | null {
  if ( !control.value ) {
    return null;
  }

  if ( !/\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b/.test(control.value)) {
    return {
      ipAddress: true
    }
  }

  return null;
}

export function validateUrl(control: UntypedFormControl): ValidationErrors | null {
  if ( !control.value ) {
    return null;
  }

  // eslint-disable-next-line max-len
  const urlRegex = /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/;

  if ( !urlRegex.test(control.value) ) {
    return {
      url: true
    }
  }

  return null;
}

export function validateSubdomain(control: UntypedFormControl): ValidationErrors | null {
  if ( !control.value ) {
    return null;
  }

  const subdomainRegex = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

  if ( !subdomainRegex.test(control.value) ) {
    return {
      subdomain: 'must only contain lower case letters, numbers or dashes'
    }
  }

  return null;
}

export function validateUrlTrailing(control: UntypedFormControl): ValidationErrors | null {
  if ( !control.value ) {
    return null;
  }

  // eslint-disable-next-line max-len
  const urlRegex = /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)\//;

  if ( !urlRegex.test(control.value) ) {
    return {
      urlTrailing: 'must be a valid url with a trailing /'
    }
  }

  return null;
}

export function validateMetaswitchPassword(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value) {
    return { 'validateMetaswitchPassword.length': true };
  }
  if (control.value.length < 8 || control.value.length > 15) {
    return { 'validateMetaswitchPassword.length': true };
  }
  if ( !/.*[A-Z].*/.test(control.value ) ) {
    return { 'validateMetaswitchPassword.uppercase': true };
  }
  if ( !/.*[0-9].*/.test(control.value ) ) {
    return { 'validateMetaswitchPassword.number': true };
  }
  if ( !/.*[~!@#$%^&*_\-+={[}\]|:;<,>.?].*/.test(control.value) ) {
    return { 'validateMetaswitchPassword.special': true };
  }
  if ( /.*[()].*/.test(control.value) ) {
    return { 'validateMetaswitchPassword.noparens': true };
  }
  // no repeat of 3 characters
  if ( /(.)\1{2,}/.test(control.value) ) {
    return { 'validateMetaswitchPassword.norepeat': true };
  }
  return null;
}

export function validateMetaswitchPIN(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value) {
    return { 'validateMetaswitchPIN.length': '// must be 4 digits'};
  }
  if ( !control.value.match(/^[0-9]*$/) ) {
    return { 'validateMetaswitchPIN.digits': '// must contain only digits'};
  }
  if (control.value.length !== 4) {
    return { 'validateMetaswitchPIN.length': '// must be 4 digits'};
  }

  // no repeat of 3 digits
  if ( /(.)\1{2,}/.test(control.value) ) {
    return { 'validateMetaswitchPIN.norepeat': '// cannot contain 3 or more repeating digits'};
  }

  // no sequences of 3 digits
  if ( checkSequentialDigits(3, control.value) ) {
    return { 'validateMetaswitchPIN.nosequence': '// cannot contain 3 or more sequential digits'};
  }

  return null;
}

export function validateMetaswitchVoicemailPIN(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value) {
    return { 'validateMetaswitchPIN.voicemailPINLength': '// must be between 6 and 20 digits'};
  }
  if ( !control.value.match(/^[0-9]*$/) ) {
    return { 'validateMetaswitchPIN.digits': '// must contain only digits'};
  }
  if (control.value.length < 6 || control.value.length > 20) {
    return { 'validateMetaswitchPIN.voicemailPINLength': '// must be 4 digits'};
  }

  // no repeat of 3 digits
  if ( /(.)\1{2,}/.test(control.value) ) {
    return { 'validateMetaswitchPIN.norepeat': '// cannot contain 3 or more repeating digits'};
  }

  // all digits cant be in a sequence... also 7890123 not allowed
  if ( isAllSequentialDigits(control.value) ) {
    return { 'validateMetaswitchPIN.notAllsequence': '// cannot contain all sequential digits'};
  }

  return null;
}

export function checkSequentialDigits(count: number, str: string): boolean {
  if (str.length < count || count < 2) {
    return false;
  }

  const nums: number[] = [];
  for (let i = 0; i < str.length; i++) {
    nums.push(+str[i]);
    if ( i >= (count - 1) ) {
      let seq = true;
      for (let j = 0; j < (count - 1); j++) {
        if (nums[i - j] !== nums[i - j - 1] + 1) {
          seq = false;
        }
      }
      if (seq) {
        return true;
      }
    }
  }

  return false;
}

export function validRange(range: number[]): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    const isEmpty = (control.value === null || control.value === '');
    const inRange = +control.value >= range[0] && +control.value <= range[1];
    if (isEmpty || !inRange) {
      return {
        'validRange': {
          minRange: range[0],
          maxRange: range[1]
        }
      };
    }

    return null;
  }
}

export function validSelection(choices: any[]): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    const value = control.value;
    return !choices.find(x => x === value)
      ? { 'validSelection': '// must be selected from choices' }
      : null;
  }
}

export function validSelectionNotRequired(choices: any[]): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    const value = control.value;
    if (!value) { return null; }
    return !choices.find(x => x === value)
      ? { 'validSelectionNotRequired': '// must be selected from choices if provided' }
      : null;
  }
}

function isValidMetaExtension(value: string): ValidationErrors | null {
  if (!value) {
    return { 'validateMetaswitchExtension.length': '// must be between 3 and 5 digits'};
  }
  if ( !value.match(/^[0-9]*$/) ) {
    return { 'validateMetaswitchExtension.digits': '// must contain only digits'};
  }
  if (value.length < 3 || value.length > 5) {
    return { 'validateMetaswitchExtension.length': '// must be between 3 and 5 digits'};
  }

  // must not start with x11
  if ( value.match(/^[0-9]11/) ) {
    return { 'validateMetaswitchExtension.nox11': '// may not start with x11' };
  }

  return null;
}

export function validateMetaswitchExtension(control: UntypedFormControl): ValidationErrors | null {
  return isValidMetaExtension(control.value);
}

export function validateMetaswitchExtensionNotRequired(control: UntypedFormControl): ValidationErrors | null {
  return !control.value ? null : isValidMetaExtension(control.value);
}

export function validateMACAddress(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }
  if (
    control.value.match(
    // eslint-disable-next-line max-len
    /^[0-9A-Fa-f][0-9A-Fa-f]:[0-9A-Fa-f][0-9A-Fa-f]:[0-9A-Fa-f][0-9A-Fa-f]:[0-9A-Fa-f][0-9A-Fa-f]:[0-9A-Fa-f][0-9A-Fa-f]:[0-9A-Fa-f][0-9A-Fa-f]$/
    ) ||
    control.value.match(/^[0-9A-Fa-f]{12}$/)
  ) {
    return null;
  }

  return {
    validateMACAddress: 'must be a valid MAC Address (xx:xx:xx:xx:xx:xx or xxxxxxxxxxxx)'
  };
}

export function validateAlphaNumericWithSpaces(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }
  if (
    control.value.match(/^[a-zA-Z0-9 ]+$/)
  ) {
    return null;
  }

  return {
    validateAlphaNumericWithSpaces: 'must contain only alphanumeric chars and spaces'
  };
}

export function validateEmailAddresses(control: UntypedFormControl): ValidationErrors | null {
  if (_.isEmpty(control.value)) {
    return null;
  }

  if (control.value && _.isArray(control.value)) {
    const checks: boolean[] = control.value.map((x: string) =>
      !!x.match(
        // eslint-disable-next-line max-len
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    );
    if (checks.findIndex(x => !x) === -1) {
      return null;
    }
  }

 return {
  validateEmailAddresses: 'must contain valid email addresses'
 };
}

const basicRegexThatDoesntGetPlus = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,8}$/;

export function validateEmails(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value) return null;
  const emails = control.value.toLowerCase().replace(/\n/g, ',')
      .split(',').map((t: string) => t.trim()).filter((t: string) => t);
  if (emails.length === 0 || emails.some((v: string) => !v.match(emailRegex))) {
    return {
      emails: 'must contain valid email addresses'
     };
  }
  else {
    return null;
  }
}

export function zipCode(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value || control.value.match(/^\d{5}(-\d{4})?$/)) {
    return null;
  }

  return {
   zipCode: 'must be 5 digits with optional dash 4 digits'
 }
}

export function addressSimple(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value || control.value.match(/^[0-9]+ .+$/)) {
    return null;
  }

  return {
    addressSimple: 'must contain house number and street'
  }
}

export function letterRange(args: [number, number]): ValidatorFn {
  const min = args[0];
  const max = args[1];
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || control.value.length < min || control.value.length > max || !control.value.match(/^[a-zA-Z]*$/)) {
        return {
          letterRange: {
            min, max
          }
        };
    }
    return null;
  };
}

export function time12hour(control: UntypedFormControl): ValidationErrors | null {
  const error = {
    time12hour: 'must be time in 12 hour format with am or pm'
  };

  if (!control.value) {
    return null;
  }

  let time = control.value.split(':');
  if (time.length !== 2 || isNaN(+time[0]) || +time[0] > 12 || +time[0] < 1) {
    return error;
  }
  if (!time[0].match(/^[0-1][0-9]$/) && !time[0].match(/^[1-9]$/))  {
    return error;
  }

  time = time[1].split(' ');
  if (time.length !== 2 || isNaN(+time[0]) || +time[0] > 59 || +time[0] < 0 || time.length !== 2)  {
    return error;
  }
  if (!time[0].match(/^[0-5][0-9]$/))  {
    return error;
  }
  if (+time[0] < 10 && !time[0].match(/^0[0-9]$/))  {
    return error;
  }
  if (_.toUpper(time[1]) !== 'AM' && _.toUpper(time[1]) !== 'PM') {
    return error;
  }

  return null;
}

export function bandwidthPortTime(control: UntypedFormControl): ValidationErrors | null {
  const formatError = {
    bandwidthPortTime: 'must be time in 12 hour format with am or pm'
  };

  const rangeError = {
    bandwidthPortTime: 'must be between 7:00 am CST and 7:00 pm CST'
  };

  if (!control.value) {
    return null;
  }

  let time = control.value.split(':');
  if (time.length !== 2 || isNaN(+time[0]) || +time[0] > 12 || +time[0] < 1) {
    return formatError;
  }
  if (!time[0].match(/^[0-1][0-9]$/) && !time[0].match(/^[1-9]$/))  {
    return formatError;
  }
  const hour = +time[0];


  time = time[1].split(' ');
  if (time.length !== 2 || isNaN(+time[0]) || +time[0] > 59 || +time[0] < 0 || time.length !== 2)  {
    return formatError;
  }
  if (!time[0].match(/^[0-5][0-9]$/))  {
    return formatError;
  }
  if (+time[0] < 10 && !time[0].match(/^0[0-9]$/))  {
    return formatError;
  }
  if (_.toUpper(time[1]) !== 'AM' && _.toUpper(time[1]) !== 'PM') {
    return formatError;
  }
  const minutes = +time[0];
  const amPm = _.toUpper(time[1]);

  if (amPm === 'AM') {
    if (hour < 7 || hour === 12) {
      return rangeError;
    }
  }

  if (amPm === 'PM') {
    if ((hour > 7 && hour !== 12) || (hour === 7 && minutes > 0)) {
      return rangeError;
    }
  }

  return null;
}

export function brightlinkPortTime(control: UntypedFormControl): ValidationErrors | null {
  const formatError = {
    brightlinkPortTime: 'must be 12 hour format with am or pm'
  };

  const rangeError = {
    brightlinkPortTime: 'must be between 7:00 am and 5:00 pm Mon-Fri CST'
  };

  if (!control.value) {
    return null;
  }

  let time = control.value.split(':');
  if (time.length !== 2 || isNaN(+time[0]) || +time[0] > 12 || +time[0] < 1) {
    return formatError;
  }
  if (!time[0].match(/^[0-1][0-9]$/) && !time[0].match(/^[1-9]$/))  {
    return formatError;
  }
  const hour = +time[0];


  time = time[1].split(' ');
  if (time.length !== 2 || isNaN(+time[0]) || +time[0] > 59 || +time[0] < 0 || time.length !== 2)  {
    return formatError;
  }
  if (!time[0].match(/^[0-5][0-9]$/))  {
    return formatError;
  }
  if (+time[0] < 10 && !time[0].match(/^0[0-9]$/))  {
    return formatError;
  }
  if (_.toUpper(time[1]) !== 'AM' && _.toUpper(time[1]) !== 'PM') {
    return formatError;
  }
  const minutes = +time[0];
  const amPm = _.toUpper(time[1]);

  if (amPm === 'AM') {
    if ((hour < 7) || hour === 12) {
      return rangeError;
    }
  }

  if (amPm === 'PM') {
    if ((hour > 5 && hour !== 12) || (hour === 5 && minutes > 0)) {
      return rangeError;
    }
  }

  return null;
}

function splitHourMins(time: string) {
  const hourMinSplit = _.split(time, ':');
  return {
    hour: _.get(hourMinSplit, '0', '0'),
    mins: _.pad(_.get(hourMinSplit, '1', '0'), 2, '0')
  }
}

export function timeRange(args: [string, string]): ValidatorFn {
  const min24 = args[0];
  const max24 = args[1];
  const { hour: minHour, mins: minMins } = splitHourMins(min24);
  const { hour: maxHour, mins: maxMins } = splitHourMins(max24);

 const formatError = {
    timeRange: {
      minTime: `${+minHour > 12 ? +minHour - 12: minHour}:${minMins} ${+minHour >= 12 && +minHour !== 24 ? 'PM' : 'AM'}`,
      maxTime: `${+maxHour > 12 ? +maxHour - 12: maxHour}:${maxMins} ${+maxHour >= 12 && +minHour !== 24 ? 'PM' : 'AM'}`
    }
  };

  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    let time = control.value.split(':');
    if (time.length !== 2 || isNaN(+time[0]) || +time[0] > 12 || +time[0] < 1) {
      return formatError;
    }
    if (!time[0].match(/^[0-1][0-9]$/) && !time[0].match(/^[1-9]$/))  {
      return formatError;
    }
    const hour = +time[0];


    time = time[1].split(' ');
    if (time.length !== 2 || isNaN(+time[0]) || +time[0] > 59 || +time[0] < 0 || time.length !== 2)  {
      return formatError;
    }
    if (!time[0].match(/^[0-5][0-9]$/))  {
      return formatError;
    }
    if (+time[0] < 10 && !time[0].match(/^0[0-9]$/))  {
      return formatError;
    }
    if (_.toUpper(time[1]) !== 'AM' && _.toUpper(time[1]) !== 'PM') {
      return formatError;
    }
    const minutes = +time[0];
    const amPm = _.toUpper(time[1]);
    const hour24 = ((amPm === 'AM' && hour !== 12) || (amPm === 'PM' && hour === 12)) ? hour : hour + 12;

    if (hour24 < +minHour || hour24 > +maxHour) {
      return formatError;
    }

    if (hour24 === +minHour && minutes < +minMins) {
      return formatError;
    }

    if (hour24 === +maxHour && minutes > +maxMins) {
      return formatError;
    }

    return null;
  }
}

export function creditCard(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value || validateCard(control.value)) {
    return null;
  }

  return {
   creditCard: 'must be a valid card number. (Visa, Mastercard, Amex, Discover)'
 }
}

const acceptedCreditCards = {
  visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
  mastercard: /^5[1-5][0-9]{14}$|^2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))[0-9]{12}$/,
  amex: /^3[47][0-9]{13}$/,
  discover: /^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
  // diners_club: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
  // jcb: /^(?:2131|1800|35[0-9]{3})[0-9]{11}$/
};

function validateCard(value: string): boolean {
  // Accept deliberate formatting only (spaces and hyphens). Any other non-digit
  // means this is not a card number — stripping it would launder arbitrary
  // embedded text (e.g. '4111x1111x1111x1111') into a valid-looking number.
  const cardNumber = value.replace(/[ -]/g, '');
  if (!/^[0-9]+$/.test(cardNumber)) {
    return false;
  }

  let sum = 0;
  let shouldDouble = false;

  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cardNumber.charAt(i));

    if (shouldDouble) {
      if ((digit *= 2) > 9) digit -= 9;
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  const valid = (sum % 10) == 0;
  let accepted = false;

  Object.values(acceptedCreditCards).forEach((regex) => {
    if (regex.test(cardNumber)) {
      accepted = true;
    }
  });

  return valid && accepted;
}

function validateCVV(cardNumber: string, cvv: string): boolean {
  if (!cardNumber) { return false }
  const card = cardNumber.replace(/\D/g, '');
  const cvvDigits = cvv.replace(/\D/g, '');

  // american express and cvv is 4 digits
  if ((acceptedCreditCards.amex).test(card)) {
    if((/^\d{4}$/).test(cvvDigits))
      return true;
  } else if ((/^\d{3}$/).test(cvvDigits)) { // other card & cvv is 3 digits
    return true;
  }
  return false;
}

export function cvv(control: UntypedFormControl): ValidationErrors | null {
  const cardNumberControl = control.root.get('cardNumber');
  if (!control.value || validateCVV(cardNumberControl?.value, control.value)) {
    return null;
  }

  return {
   cvv: 'must be a 3 or 4 digit number.'
 }
}

export function validateCheckSelectRequired(control: UntypedFormControl): ValidationErrors | null {
  const values = checkSelectValues(control.value);

  if (values.length  > 0) {
    return null;
  }

  return {
    requiredCheckSelect: 'At least one option must be selected'
  }
}

export function allOrNothing(control: UntypedFormControl): ValidationErrors | null {
  const values = control.value;
  if (!values) {
    return null;
  }

  const keys = Object.keys(values);

  const allFilled = keys.every(key => values[key] != null && values[key] !== '');
  const allEmpty = keys.every(key => values[key] == null || values[key] === '');

  if (allFilled || allEmpty) {
    return null;
  }

  return {
    allOrNothing: 'Requires all field values'
  }
}

export function notBlank(control: UntypedFormControl): ValidationErrors | null {
  if (!control.value || typeof control.value !== 'string') {
    return null;
  }
  if (control.value.trim().length > 0) {
    return null;
  }

  return {
    notBlank: true
  };
}
